import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PricingCalendar.css';
import { getReservationById, Reservation } from '../api.reservations';
import { getApartments, Apartment } from '../api.apartments';
import {
  getCalendarData,
  upsertPricing,
  deletePricing,
  CalendarData,
  CustomPrice,
  ReservationSummary,
  PricingUpsertPayload,
} from '../api.pricingCalendar';
import { getStoredAuthState } from '../auth';
import { MONTHS_CS, DOW_SHORT_CS, addDays, diffDays, fmtShort, fmtLong, todayStr } from './calendar/dateUtils';
import { CHECKIN_OFFSET, CHECKOUT_OFFSET, PAST_RESERVATION_COLORS, platformColors } from './calendar/reservationVisuals';
import { parseDowPrices, resolveDayPrice } from './calendar/pricing';
import MultiApartmentTimeline, { MultiApartmentTimelineHandle } from './calendar/MultiApartmentTimeline';
import { DIRECT_BOOKING_RELEASED } from '../featureFlags';

const getToken = () => localStorage.getItem('token') || '';

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function statusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'CONFIRMED': return '✅ Potvrzeno';
    case 'PENDING': return '⏳ Čeká na schválení';
    case 'OVERBOOKED': return '⚠️ Přebukováno';
    case 'CANCELLED': return '❌ Zrušeno';
    default: return status || '—';
  }
}
function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return {
    from: `${year}-${String(month + 1).padStart(2, '0')}-01`,
    to: `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}

// ─── Panel state ─────────────────────────────────────────────────
type PanelMode = 'single' | 'range' | 'reservation';
interface PanelState {
  mode: PanelMode;
  anchorDate: string;
  res?: ReservationSummary;
}

// ─── Component ───────────────────────────────────────────────────
const PricingCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedAptId, setSelectedAptId] = useState<number | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [calData, setCalData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(false);

  // Range selection
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [rangeEnd, setRangeEnd] = useState<string | null>(null);
  const [rangeAptId, setRangeAptId] = useState<number | null>(null);

  // View mode: single apartment (today's grid) vs. all apartments (timeline).
  // Manager-only feature — force back to 'single' if role isn't MANAGER, even if a stale
  // 'all' value is sitting in localStorage from a previous session on a shared machine.
  const isManager = getStoredAuthState().userRole === 'MANAGER';
  const [viewMode, setViewModeState] = useState<'single' | 'all'>(() =>
    isManager && localStorage.getItem('calendar_view_mode') === 'all' ? 'all' : 'single'
  );
  const timelineRef = useRef<MultiApartmentTimelineHandle>(null);

  // Panel
  const [panel, setPanel] = useState<PanelState | null>(null);
  const [fieldPrice, setFieldPrice] = useState('');
  const [fieldMinN, setFieldMinN] = useState('');
  const [fieldNote, setFieldNote] = useState('');
  const [fieldPromo, setFieldPromo] = useState('');
  const [promoOn, setPromoOn] = useState(false);
  const [blockOn, setBlockOn] = useState(false);

  // Full reservation detail (fetched on open)
  const [resDetail, setResDetail] = useState<Reservation | null>(null);
  const [resDetailLoading, setResDetailLoading] = useState(false);

  // Toast
  const [toastMsg, setToastMsg] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  // ─── Load apartments ───────────────────────────────────────────
  useEffect(() => {
    getApartments(getToken()).then((apts: Apartment[]) => {
      setApartments(apts);
      if (apts.length > 0 && apts[0].id != null) setSelectedAptId(apts[0].id!);
    }).catch(() => { });
  }, []);

  // ─── Load calendar data (single-apartment mode only) ───────────
  useEffect(() => {
    if (viewMode !== 'single' || selectedAptId == null) return;
    const { from, to } = monthRange(year, month);
    setLoading(true);
    getCalendarData(selectedAptId, from, to, getToken())
      .then(data => { setCalData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [viewMode, selectedAptId, year, month]);

  // ─── Derived maps ──────────────────────────────────────────────
  const dowPrices = useMemo<Record<number, number>>(
    () => parseDowPrices(calData?.raisedPriceJson),
    [calData?.raisedPriceJson]
  );

  const customPriceMap = useMemo<Record<string, CustomPrice>>(() => {
    if (!calData) return {};
    return Object.fromEntries(calData.customPrices.map(p => [p.date, p]));
  }, [calData]);

  // ─── Reservation overlay (drawn after render) ──────────────────
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || !calData) return;

    type BandItem = { cell: HTMLElement; date: string; isCheckIn: boolean; isCheckOut: boolean };


    const BAND_HEIGHT = 35;       // fixed height, shorter than full cell
    const RADIUS = 7;

    const draw = () => {
      // Mobile day cells are a fixed 58px tall (see .pc-day mobile breakpoint in
      // PricingCalendar.css) vs. 84px on desktop — re-checked per draw so it tracks
      // viewport width changes via the ResizeObserver below.
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const V_TOP = isMobile ? 35 : 50; // px from cell top — day number + price stay fully clear

      grid.querySelectorAll('.pc-res-overlay').forEach(el => el.remove());

      for (const res of calData.reservations) {
        // Collect occupied night cells + the checkout cell (departure morning)
        const items: BandItem[] = [];
        let d = res.checkIn;
        while (d < res.checkOut) {
          const cell = grid.querySelector<HTMLElement>(`[data-date="${d}"]`);
          if (cell && !cell.classList.contains('pc-other')) {
            items.push({ cell, date: d, isCheckIn: d === res.checkIn, isCheckOut: false });
          }
          d = addDays(d, 1);
        }
        const coCell = grid.querySelector<HTMLElement>(`[data-date="${res.checkOut}"]`);
        if (coCell && !coCell.classList.contains('pc-other')) {
          items.push({ cell: coCell, date: res.checkOut, isCheckIn: false, isCheckOut: true });
        }
        if (!items.length) continue;

        // Group by calendar row (same offsetTop)
        const byRow = new Map<number, BandItem[]>();
        for (const item of items) {
          const top = item.cell.offsetTop;
          if (!byRow.has(top)) byRow.set(top, []);
          byRow.get(top)!.push(item);
        }

        const rows = Array.from(byRow.entries()).sort((a, b) => a[0] - b[0]);

        rows.forEach(([, rowItems], ri) => {
          const hasCheckIn = rowItems.some(it => it.isCheckIn);
          const hasCheckOut = rowItems.some(it => it.isCheckOut);
          const checkInItem = rowItems.find(it => it.isCheckIn);
          const checkOutItem = rowItems.find(it => it.isCheckOut);

          const refCell = rowItems[0].cell;
          const bandTop = refCell.offsetTop + V_TOP;
          const bandHeight = Math.min(BAND_HEIGHT, refCell.offsetHeight - V_TOP - 6);
          if (bandHeight <= 0) return;

          // Start: offset into check-in cell; otherwise from left edge of first cell
          const startX = hasCheckIn
            ? checkInItem!.cell.offsetLeft + checkInItem!.cell.offsetWidth * CHECKIN_OFFSET
            : refCell.offsetLeft;

          // End: into check-out cell; otherwise right edge of last occupied cell
          let endX: number;
          if (hasCheckOut) {
            endX = checkOutItem!.cell.offsetLeft + checkOutItem!.cell.offsetWidth * CHECKOUT_OFFSET;
          } else {
            const lastOcc = rowItems.filter(it => !it.isCheckOut).slice(-1)[0]?.cell ?? refCell;
            endX = lastOcc.offsetLeft + lastOcc.offsetWidth;
          }

          const width = endX - startX;
          if (width <= 0) return;

          const tl = hasCheckIn ? RADIUS : 0;
          const tr = hasCheckOut ? RADIUS : 0;

          const isPast = res.checkOut < todayStr();
          const col = isPast ? PAST_RESERVATION_COLORS : platformColors(res.platform);
          const ol = document.createElement('div');
          ol.className = isPast ? 'pc-res-overlay pc-res-overlay-past' : 'pc-res-overlay';
          ol.style.top = bandTop + 'px';
          ol.style.left = startX + 'px';
          ol.style.width = width + 'px';
          ol.style.height = bandHeight + 'px';
          ol.style.background = col.bg;
          ol.style.borderTop = `2px solid ${col.border}`;
          ol.style.borderBottom = `2px solid ${col.border}`;
          ol.style.borderLeft = hasCheckIn ? `2px solid ${col.border}` : 'none';
          ol.style.borderRight = hasCheckOut ? `2px solid ${col.border}` : 'none';
          ol.style.borderRadius = `${tl}px ${tr}px ${tr}px ${tl}px`;

          if (ri === 0) {
            const nameEl = document.createElement('div');
            nameEl.className = 'pc-res-overlay-name';
            nameEl.style.color = col.text;
            const guests = res.numberOfGuests ? ` · ${res.numberOfGuests} os.` : '';
            nameEl.textContent = '▸ ' + (res.guestName || '—') + guests;
            ol.appendChild(nameEl);
          }

          grid.appendChild(ol);
        });
      }
    };

    const ro = new ResizeObserver(() => requestAnimationFrame(draw));
    ro.observe(grid);
    requestAnimationFrame(draw);
    return () => ro.disconnect();
  }, [calData, rangeStart, rangeEnd]);

  // ─── Keyboard close ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ─── Helpers ───────────────────────────────────────────────────
  const showToast = (msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2500);
  };

  const refreshData = async () => {
    if (selectedAptId == null) return;
    if (viewMode === 'all') {
      await timelineRef.current?.refreshApartment(selectedAptId);
      return;
    }
    const { from, to } = monthRange(year, month);
    const data = await getCalendarData(selectedAptId, from, to, getToken());
    setCalData(data);
  };

  const switchViewMode = (mode: 'single' | 'all') => {
    setViewModeState(mode);
    localStorage.setItem('calendar_view_mode', mode);
    closePanel();
  };

  const closePanel = () => {
    setPanel(null);
    setRangeStart(null);
    setRangeEnd(null);
    setRangeAptId(null);
    setResDetail(null);
  };


  // ─── Panel openers ─────────────────────────────────────────────
  // `customOverride`: in "all apartments" mode, calData/customPriceMap here reflects the single-
  // apartment grid's last-loaded apartment (or nothing), not whichever apartment's row was
  // actually clicked in the timeline — so the timeline passes its own already-correct per-cell
  // value instead of letting this fall back to the (potentially wrong) local lookup.
  const openSingle = (dateStr: string, customOverride?: CustomPrice) => {
    const custom = customOverride !== undefined ? customOverride : customPriceMap[dateStr];
    setFieldPrice(custom?.pricePerNight != null ? String(custom.pricePerNight) : '');
    setFieldMinN(custom?.minNights != null ? String(custom.minNights) : '');
    setFieldNote(custom?.note ?? '');
    setPromoOn(!!custom?.promoPrice);
    setFieldPromo(custom?.promoPrice != null ? String(custom.promoPrice) : '');
    setBlockOn(!!custom?.isBlocked);
    setPanel({ mode: 'single', anchorDate: dateStr });
  };

  const openRange = (a: string, b: string) => {
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    setFieldPrice(''); setFieldMinN(''); setFieldNote('');
    setPromoOn(false); setFieldPromo(''); setBlockOn(false);
    setRangeStart(lo); setRangeEnd(hi);
    setPanel({ mode: 'range', anchorDate: lo });
  };

  const openReservation = (res: ReservationSummary, dateStr: string) => {
    setRangeStart(null); setRangeEnd(null); setRangeAptId(null);
    setResDetail(null);
    setPanel({ mode: 'reservation', anchorDate: dateStr, res });
    setResDetailLoading(true);
    getReservationById(res.id, getToken())
      .then(full => setResDetail(full))
      .catch(() => { })
      .finally(() => setResDetailLoading(false));
  };

  // ─── Click handlers ─────────────────────────────────────────────
  // Shared by the single-apartment grid and the multi-apartment timeline. `apartmentId` only
  // matters in "all apartments" mode — the single grid always passes the one selected apartment,
  // so a pending range anchor there is trivially "same apartment." In multi mode, `rangeAptId`
  // stops a pending anchor on one apartment's row from being misread as completing a range when
  // the next click lands on a *different* apartment's row.
  const handleDayClick = (apartmentId: number, dateStr: string, customOverride?: CustomPrice) => {
    const sameApartment = rangeAptId === apartmentId;
    if (!sameApartment || !rangeStart) {
      setSelectedAptId(apartmentId);
      setRangeAptId(apartmentId);
      setRangeStart(dateStr);
      setRangeEnd(null);
      openSingle(dateStr, customOverride);
    } else if (!rangeEnd && dateStr !== rangeStart) {
      openRange(rangeStart, dateStr);
    } else {
      setRangeAptId(apartmentId);
      setRangeStart(dateStr);
      setRangeEnd(null);
      openSingle(dateStr, customOverride);
    }
  };

  const handleTimelineReservationClick = (apartmentId: number, res: ReservationSummary, dateStr: string) => {
    setSelectedAptId(apartmentId);
    openReservation(res, dateStr);
  };

  // ─── Save ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (selectedAptId == null || !panel) return;
    const price = blockOn ? null : (parseFloat(fieldPrice) || null);
    const minN = blockOn ? null : (parseInt(fieldMinN, 10) || null);
    const promo = blockOn ? null : (promoOn ? (parseFloat(fieldPromo) || null) : null);
    if (!blockOn && !price && !minN) { showToast('Zadejte alespoň cenu nebo min. nocí.'); return; }

    let dates: string[];
    if (panel.mode === 'range' && rangeStart && rangeEnd) {
      dates = [];
      const [lo, hi] = rangeStart <= rangeEnd ? [rangeStart, rangeEnd] : [rangeEnd, rangeStart];
      let d = lo;
      while (d <= hi) { dates.push(d); d = addDays(d, 1); }
    } else {
      dates = [panel.anchorDate];
    }

    const payload: PricingUpsertPayload = {
      dates,
      pricePerNight: price as any,
      minNights: minN,
      promoPrice: promo as any,
      note: fieldNote.trim() || null,
      isBlocked: blockOn,
    };

    try {
      await upsertPricing(selectedAptId, payload, getToken());
      await refreshData();
      closePanel();
      showToast(blockOn ? 'Termín zablokován ✓' : 'Ceny uloženy ✓');
    } catch (e: any) {
      showToast('Chyba: ' + (e?.message ?? 'neznámá'));
    }
  };

  const handleDelete = async () => {
    if (selectedAptId == null || !panel) return;
    try {
      await deletePricing(selectedAptId, panel.anchorDate, getToken());
      await refreshData();
      closePanel();
      showToast('Vlastní cena smazána');
    } catch (e: any) {
      showToast('Chyba: ' + (e?.message ?? 'neznámá'));
    }
  };

  // ─── Sorted range ──────────────────────────────────────────────
  const [rLo, rHi] = useMemo(() => {
    if (!rangeStart || !rangeEnd) return [null, null];
    return rangeStart <= rangeEnd ? [rangeStart, rangeEnd] : [rangeEnd, rangeStart];
  }, [rangeStart, rangeEnd]);

  // ─── Build calendar cells ──────────────────────────────────────
  const today = todayStr();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Mon=0..Sun=6
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells: React.ReactNode[] = [];
  for (let i = 0; i < totalCells; i++) {
    let dateStr: string;
    let dayNum: number;
    let otherMonth = false;

    if (i < startOffset) {
      dayNum = prevMonthDays - startOffset + i + 1;
      const pm = month === 0 ? 11 : month - 1;
      const py = month === 0 ? year - 1 : year;
      dateStr = `${py}-${String(pm + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      otherMonth = true;
    } else if (i >= startOffset + daysInMonth) {
      dayNum = i - startOffset - daysInMonth + 1;
      const nm = month === 11 ? 0 : month + 1;
      const ny = month === 11 ? year + 1 : year;
      dateStr = `${ny}-${String(nm + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      otherMonth = true;
    } else {
      dayNum = i - startOffset + 1;
      dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    }

    const res = !otherMonth && calData ? (calData.reservations.find(r => r.checkIn <= dateStr && dateStr < r.checkOut) ?? null) : null;
    const { price, promo, minNights: minN, showDowTag, isBlocked } = otherMonth
      ? { price: null, promo: null, minNights: null, showDowTag: false, isBlocked: false }
      : resolveDayPrice(customPriceMap, dowPrices, calData?.basePricePerNight, dateStr);

    // CSS class composition
    const cls: string[] = ['pc-day'];
    if (otherMonth) cls.push('pc-other');
    if (dateStr === today && !otherMonth) cls.push('pc-today');
    if (dateStr < today && !otherMonth) { cls.push('pc-past'); cls.push('pc-no-booking'); }
    if (isBlocked && !otherMonth) cls.push('pc-blocked');

    let onClick: (() => void) | undefined;

    if (res && !otherMonth) {
      const capturedRes = res;
      const capturedDate = dateStr;
      onClick = () => openReservation(capturedRes, capturedDate);
    } else if (!otherMonth && calData?.hasBookingSite && dateStr >= today) {
      if (rangeStart && !rangeEnd && dateStr === rangeStart) cls.push('pc-anchor');
      else if (rLo && rHi) {
        if (dateStr === rLo || dateStr === rHi) cls.push('pc-anchor');
        else if (dateStr > rLo && dateStr < rHi) cls.push('pc-in-range');
      }
      const capturedDate = dateStr;
      onClick = () => handleDayClick(selectedAptId!, capturedDate);
    } else if (!otherMonth) {
      cls.push('pc-no-booking');
    }

    const numClass = dateStr === today && !otherMonth ? 'pc-day-num pc-day-num-today' : 'pc-day-num';

    cells.push(
      <div
        key={`${dateStr}-${i}`}
        className={cls.join(' ')}
        data-date={dateStr}
        onClick={onClick}
      >
        <div className={numClass}>{dayNum}</div>
        {!res && !otherMonth && isBlocked && <div className="pc-blocked-icon">🚫</div>}
        {!res && !otherMonth && !isBlocked && price != null && (
          promo != null && promo < price ? (
            <>
              <div className="pc-price-orig">{Number(price).toLocaleString('cs')}<span className="pc-currency"> Kč</span></div>
              <div className="pc-price-promo">{Number(promo).toLocaleString('cs')}<span className="pc-currency"> Kč</span></div>
            </>
          ) : (
            <div className="pc-price">{Number(price).toLocaleString('cs')}<span className="pc-currency"> Kč</span></div>
          )
        )}
        {!res && !otherMonth && !isBlocked && showDowTag && <div className="pc-dow-tag">↑</div>}
        {!res && !otherMonth && !isBlocked && minN != null && minN > 1 && <div className="pc-min-nights">🌙 {minN}</div>}
      </div>
    );
  }

  // ─── Derived panel labels ──────────────────────────────────────
  const panelTitle = panel?.mode === 'single' ? 'Upravit den'
    : panel?.mode === 'range' ? 'Upravit rozsah'
      : 'Detail rezervace';

  const panelDateLabel = !panel ? '' :
    panel.mode === 'range' && rLo && rHi ? `${fmtShort(rLo)} – ${fmtShort(rHi)}`
      : fmtLong(panel.anchorDate);

  const rangeCount = rLo && rHi ? diffDays(rLo, rHi) + 1 : 0;
  const hasCustom = panel ? !!customPriceMap[panel.anchorDate] : false;

  // Only meaningful in "all apartments" mode — in single mode the apartment is already obvious
  // from the page-level dropdown above.
  const activeAptName = viewMode === 'all' && selectedAptId != null
    ? apartments.find(a => a.id === selectedAptId)?.internalName
    ?? apartments.find(a => a.id === selectedAptId)?.name
    ?? null
    : null;

  const customCountMonth = Object.keys(customPriceMap).filter(k => {
    const d = new Date(k + 'T00:00:00');
    return d.getFullYear() === year && d.getMonth() === month;
  }).length;

  const resCountMonth = calData?.reservations.filter(r => {
    const d = new Date(r.checkIn + 'T00:00:00');
    return d.getFullYear() === year && d.getMonth() === month;
  }).length ?? 0;

  const mainClass = panel ? 'pc-main pc-panel-open-main' : 'pc-main';

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {/* Header */}
      <div className="pc-page-header">
        <div>
          <h2 className="pc-page-title">Kalendář</h2>
          <p className="pc-page-subtitle">Přehled rezervací a správa cen pro přímé rezervace</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {isManager && (
            <div className="pc-view-toggle">
              <button
                className={viewMode === 'single' ? 'pc-view-btn pc-view-btn-active' : 'pc-view-btn'}
                onClick={() => switchViewMode('single')}
              >
                Jeden byt
              </button>
              <button
                className={viewMode === 'all' ? 'pc-view-btn pc-view-btn-active' : 'pc-view-btn'}
                onClick={() => switchViewMode('all')}
              >
                Všechny byty
              </button>
            </div>
          )}
          {viewMode === 'single' && (
            <div className="pc-apt-selector">
              <span>🏠</span>
              <select
                value={selectedAptId ?? ''}
                onChange={e => { setSelectedAptId(Number(e.target.value)); closePanel(); setCalData(null); }}
              >
                {apartments.map(a => (
                  <option key={a.id} value={a.id}>{a.internalName || a.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {viewMode === 'all' ? (
        <MultiApartmentTimeline
          ref={timelineRef}
          apartments={apartments}
          rangeAptId={rangeAptId}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          onDayClick={handleDayClick}
          onReservationClick={handleTimelineReservationClick}
        />
      ) : (
        <div className={mainClass}>
          {/* No-booking-site banner — suppressed while direct booking is unreleased (every
              apartment currently lacks a site, so this would show for everyone). See
              featureFlags.ts. */}
          {DIRECT_BOOKING_RELEASED && calData && !calData.hasBookingSite && (
            <div className="pc-no-site">
              Tento byt nemá aktivní stránku přímého rezervace — zobrazuji pouze rezervace. Ceny a nastavení jsou dostupné po vytvoření stránky v záložce <strong>Přímé rezervace</strong>.
            </div>
          )}

          {/* Stats */}
          {calData?.hasBookingSite && (
            <div className="pc-stats-row">
              <div className="pc-stat">
                <div className="pc-stat-label">Základní cena</div>
                <div className="pc-stat-value" style={{ color: 'var(--success)' }}>
                  {calData.basePricePerNight != null ? `${Number(calData.basePricePerNight).toLocaleString('cs')} Kč` : '—'}
                </div>
              </div>
              <div className="pc-stat">
                <div className="pc-stat-label">Vlastní ceny (tento měs.)</div>
                <div className="pc-stat-value" style={{ color: 'var(--brand)' }}>{loading ? '…' : `${customCountMonth} dní`}</div>
              </div>
              <div className="pc-stat">
                <div className="pc-stat-label">Rezervace (tento měs.)</div>
                <div className="pc-stat-value">{loading ? '…' : resCountMonth}</div>
              </div>
            </div>
          )}
          {calData && !calData.hasBookingSite && (
            <div className="pc-stats-row">
              <div className="pc-stat">
                <div className="pc-stat-label">Rezervace (tento měs.)</div>
                <div className="pc-stat-value">{loading ? '…' : resCountMonth}</div>
              </div>
            </div>
          )}

          {/* Month nav */}
          <div className="pc-month-nav">
            <button className="pc-nav-btn" onClick={() => {
              if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1);
            }}>‹</button>
            <div className="pc-month-label">{MONTHS_CS[month]} {year}</div>
            <button className="pc-nav-btn" onClick={() => {
              if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1);
            }}>›</button>
            {calData?.hasBookingSite && <span className="pc-currency-badge">Kč</span>}
          </div>

          {/* Day-of-week header */}
          <div className="pc-cal-header">
            {DOW_SHORT_CS.map(d => (
              <div key={d} className="pc-cal-header-day">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="pc-cal-grid" ref={gridRef}>
            {loading
              ? <div className="pc-loading">Načítám…</div>
              : cells
            }
          </div>

          {/* Legend */}
          <div className="pc-legend">
            <div className="pc-legend-item"><div className="pc-legend-swatch pc-ls-free" />Dostupný den</div>
            <div className="pc-legend-item">
              <div style={{ display: 'inline-flex', gap: 3 }}>
                <div style={{ width: 14, height: 14, borderRadius: 3, background: '#fdeae7', border: '1.5px solid #eca898' }} />
                <div style={{ width: 14, height: 14, borderRadius: 3, background: '#dbeafe', border: '1.5px solid #93c5fd' }} />
                <div style={{ width: 14, height: 14, borderRadius: 3, background: '#dcfce7', border: '1.5px solid #86efac' }} />
              </div>
              &nbsp;Rezervace (barva dle zdroje)
            </div>
            <div className="pc-legend-item">
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, background: 'var(--warning)', color: '#fff', borderRadius: '50%', fontSize: 10, fontWeight: 700 }}>7</span>
              &nbsp;Dnes
            </div>
            {calData?.hasBookingSite && <>
              <div className="pc-legend-item">
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)' }}>↑</span>&nbsp;Zvýšená cena (den v týdnu)
              </div>
              <div className="pc-legend-item">
                <span style={{ textDecoration: 'line-through', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>2800</span>&nbsp;
                <span style={{ color: '#8b5500', fontWeight: 800, fontSize: 13 }}>2100 Kč</span>&nbsp;Promo
              </div>
            </>}
          </div>

          {calData?.hasBookingSite && (
            <div className="pc-hint">
              💡 Kliknutím na den otevřete editor. Klikněte na první den, pak na poslední pro hromadnou úpravu.
            </div>
          )}
        </div>
      )}

      {/* Edit panel */}
      <div className={panel ? 'pc-panel pc-panel-visible' : 'pc-panel'}>
        <div className="pc-panel-header">
          <h3 className="pc-panel-title">{panelTitle}</h3>
          <button className="pc-panel-close" onClick={closePanel}>✕</button>
        </div>

        <div className="pc-panel-body">
          <div className="pc-date-chip">📅 {panelDateLabel}{activeAptName ? ` · ${activeAptName}` : ''}</div>

          {panel?.mode === 'range' && (
            <div className="pc-range-hint">
              Vybrán rozsah <strong>{rangeCount} dní</strong>. Změny se uplatní na všechny vybrané dny.
            </div>
          )}

          {/* Reservation detail card */}
          {panel?.mode === 'reservation' && panel.res && (
            <>
              <div className="pc-res-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                  <div className="pc-res-card-guest">👤 {panel.res.guestName || '—'}</div>
                  {panel.res.platform && (
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '2px 7px', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                      {panel.res.platform}
                    </span>
                  )}
                </div>
                <div className="pc-res-card-nights">{diffDays(panel.res.checkIn, panel.res.checkOut)} nocí</div>
                <div className="pc-res-card-row">
                  <span className="pc-res-card-label">Příjezd</span>
                  <span>{fmtLong(panel.res.checkIn)}</span>
                </div>
                <div className="pc-res-card-row">
                  <span className="pc-res-card-label">Odjezd</span>
                  <span>{fmtLong(panel.res.checkOut)}</span>
                </div>
                <hr className="pc-divider" />
                <div className="pc-res-card-row">
                  <span className="pc-res-card-label">Počet hostů</span>
                  <span>{panel.res.numberOfGuests ?? '—'}</span>
                </div>
                <div className="pc-res-card-row">
                  <span className="pc-res-card-label">Stav</span>
                  <span>{statusLabel(panel.res.status)}</span>
                </div>
              </div>

              {resDetailLoading && (
                <div style={{ fontSize: '0.82rem', color: '#718096', padding: '6px 0 10px' }}>Načítám detail…</div>
              )}

              {/* TTLock PIN */}
              {resDetail?.ttlockPinCode && (
                <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>🔐 Kód zámku</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '1.2rem', fontWeight: 800, letterSpacing: '0.15em', color: '#0f172a', background: '#dcfce7', padding: '3px 12px', borderRadius: 6 }}>
                      {resDetail.ttlockPinCode}
                    </span>
                    {resDetail.ttlockPinIsOffline && (
                      <span style={{ fontSize: '0.7rem', background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>⚡ offline</span>
                    )}
                  </div>
                </div>
              )}

              {/* Guestbook / visiting monitoring */}
              {resDetail && (
                <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>📋 Registrace hostů</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: (resDetail.publicCheckinLockedAt || resDetail.formCompletedAt || resDetail.guideFirstOpenedAt) ? 10 : 0 }}>
                    {resDetail.guestbookStatus === 'COMPLETED' ? '✅ Dokončena'
                      : resDetail.guestbookStatus === 'INCOMPLETE' ? '⏳ Probíhá'
                        : '— Nevyplněno'}
                  </div>
                  {[
                    { label: '📝 Registrace zahájena', ts: resDetail.publicCheckinLockedAt },
                    { label: '✅ Vše vyplněno', ts: resDetail.formCompletedAt },
                    { label: '👁 Průvodce otevřen', ts: resDetail.guideFirstOpenedAt },
                  ].filter(e => !!e.ts).map(e => (
                    <div key={e.label} style={{ fontSize: '0.78rem', color: '#64748b', display: 'flex', gap: 6, marginBottom: 3 }}>
                      <span>{e.label}</span>
                      <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDateTime(e.ts!)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Link to full detail */}
              {resDetail && (
                <button
                  onClick={() => navigate(`/reservations/${resDetail.id}`)}
                  style={{ display: 'block', width: '100%', textAlign: 'center', padding: '9px', background: '#f1f5f9', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, color: '#1e40af', border: '1px solid #e2e8f0', cursor: 'pointer', marginBottom: 4 }}
                >
                  Otevřít plný detail →
                </button>
              )}
            </>
          )}

          {/* Pricing fields */}
          {(panel?.mode === 'single' || panel?.mode === 'range') && (
            <div>
              <div className="pc-field-section-title">Blokace termínu</div>
              <div className="pc-promo-row">
                <button
                  className={blockOn ? 'pc-toggle pc-toggle-block-on' : 'pc-toggle'}
                  onClick={() => setBlockOn(v => !v)}
                  type="button"
                >
                  <div className="pc-toggle-knob" />
                </button>
                <span className="pc-toggle-label">🚫 Blokovat termín</span>
              </div>

              <hr className="pc-divider" />

              <div style={blockOn ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
                <div className="pc-field-section-title">Cena za noc</div>
                <div className="pc-field-row">
                  <div className="pc-field-group">
                    <label className="pc-field-label">Cena (Kč / noc)</label>
                    <input
                      type="number" className="pc-field-input"
                      value={fieldPrice} onChange={e => setFieldPrice(e.target.value)}
                      placeholder={calData?.basePricePerNight != null ? String(calData.basePricePerNight) : ''}
                      min={0} step={50}
                    />
                    <div className="pc-field-hint">
                      Základ: {calData?.basePricePerNight != null ? `${Number(calData.basePricePerNight).toLocaleString('cs')} Kč` : '—'}
                    </div>
                  </div>
                  <div className="pc-field-group">
                    <label className="pc-field-label">Min. nocí</label>
                    <input
                      type="number" className="pc-field-input"
                      value={fieldMinN} onChange={e => setFieldMinN(e.target.value)}
                      placeholder="1" min={1} max={30}
                    />
                    <div className="pc-field-hint">Základ: 1 noc</div>
                  </div>
                </div>

                <hr className="pc-divider" />

                <div className="pc-field-section-title">Promo / sleva</div>
                <div className="pc-promo-row">
                  <button
                    className={promoOn ? 'pc-toggle pc-toggle-on' : 'pc-toggle'}
                    onClick={() => setPromoOn(v => !v)}
                    type="button"
                  >
                    <div className="pc-toggle-knob" />
                  </button>
                  <span className="pc-toggle-label">Promo cena aktivní</span>
                </div>
                {promoOn && (
                  <div className="pc-field-group">
                    <label className="pc-field-label">Promo cena (Kč / noc)</label>
                    <input
                      type="number" className="pc-field-input"
                      value={fieldPromo} onChange={e => setFieldPromo(e.target.value)}
                      placeholder="Nižší cena…" min={0} step={50}
                    />
                    <div className="pc-field-hint">Zobrazí se přeškrtnutá původní cena + zvýrazněná promo</div>
                  </div>
                )}
              </div>

              <hr className="pc-divider" />

              <div className="pc-field-section-title">Poznámka (interní)</div>
              <div className="pc-field-group">
                <textarea
                  className="pc-field-input" rows={2}
                  value={fieldNote} onChange={e => setFieldNote(e.target.value)}
                  placeholder={blockOn ? 'Důvod blokace, např. Vlastní pobyt rodiny…' : 'Např. Velikonoce, prodloužený víkend…'}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>
          )}
        </div>

        {(panel?.mode === 'single' || panel?.mode === 'range') && (
          <div className="pc-panel-footer">
            <button className="pc-btn-primary" onClick={handleSave}>{blockOn ? 'Uložit blokaci' : 'Uložit'}</button>
            {panel?.mode === 'single' && hasCustom && (
              <button className="pc-btn-danger" onClick={handleDelete}>Smazat vlastní cenu</button>
            )}
            <button className="pc-btn-ghost" onClick={closePanel}>Zrušit</button>
          </div>
        )}
      </div>

      {toastMsg && <div className="pc-toast">{toastMsg}</div>}
    </div>
  );
};

export default PricingCalendar;
